import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useTransform, useMotionValue } from "framer-motion";

/* ---------------------------------------------------------------- */
/*  One question, many trajectories: a single signal branching into  */
/*  the different forms an idea can take.                            */
/* ---------------------------------------------------------------- */

const ENDS = [
  [560, 58],
  [560, 128],
  [560, 196],
  [560, 264],
  [560, 332],
  [560, 400],
];
const OUTCOME_WORDS = ["RESEARCH", "FRAMEWORK", "DIALOGUE", "COLLABORATION", "EXPERIMENT", "PRODUCT"];
const ORIGIN = [72, 230];

const pathFor = ([ex, ey]) => `M${ORIGIN[0]},${ORIGIN[1]} C 210,${ORIGIN[1]} 250,${ey} ${ex},${ey}`;

function BranchPath({ i, progress, assemble, delay }) {
  const fallback = useMotionValue(1);
  const prog = progress || fallback;
  const len = useTransform(prog, (v) => Math.min(1, Math.max(0, v * 1.6 - i * 0.12)));
  const nodeOpacity = useTransform(prog, [0.25 + i * 0.12, 0.35 + i * 0.12], [0, 1]);
  const [ex, ey] = ENDS[i];
  const d = pathFor(ENDS[i]);

  return (
    <>
      <motion.path
        d={d}
        fill="none"
        stroke="#0F0F0F"
        strokeOpacity="0.35"
        strokeWidth="1"
        {...(assemble
          ? { initial: { pathLength: 0 }, animate: { pathLength: 1 }, transition: { duration: 1.3, delay, ease: [0.22, 1, 0.36, 1] } }
          : { style: { pathLength: len } })}
      />
      <motion.circle
        cx={ex}
        cy={ey}
        r="3"
        fill="#1E4D38"
        {...(assemble
          ? { initial: { scale: 0, opacity: 0 }, animate: { scale: 1, opacity: 1 }, transition: { duration: 0.4, delay: delay + 1.0 } }
          : { style: { opacity: nodeOpacity } })}
      />
    </>
  );
}

function OutcomeLabel({ i, progress }) {
  const fallback = useMotionValue(0);
  const prog = progress || fallback;
  const opacity = useTransform(prog, [0.3 + i * 0.11, 0.42 + i * 0.11], [0, 1]);
  const dy = useTransform(prog, [0.3 + i * 0.11, 0.42 + i * 0.11], [6, 0]);
  const [, ey] = ENDS[i];
  return (
    <motion.text
      x="566"
      y={ey - 12}
      textAnchor="end"
      fontSize="11"
      letterSpacing="2.5"
      fill={i === 5 ? "#1E4D38" : "#6B6A65"}
      style={{ opacity, y: dy }}
    >
      {OUTCOME_WORDS[i]}
    </motion.text>
  );
}

export function InquiryTree({ progress = null, assemble = false, showLabels = false, delay = 0, className = "" }) {
  return (
    <svg viewBox="0 0 640 460" className={className} role="img" aria-label="One signal branching into multiple trajectories">
      {[115, 230, 345].map((y) => (
        <line key={y} x1="0" y1={y} x2="640" y2={y} stroke="#0F0F0F" strokeOpacity="0.05" strokeWidth="1" />
      ))}
      {ENDS.map((_, i) => (
        <BranchPath key={i} i={i} progress={progress} assemble={assemble} delay={delay + 0.5 + i * 0.16} />
      ))}
      {showLabels && ENDS.map((_, i) => <OutcomeLabel key={i} i={i} progress={progress} />)}
      {/* origin: the question */}
      <motion.circle
        cx={ORIGIN[0]}
        cy={ORIGIN[1]}
        r="5"
        fill="#1E4D38"
        initial={assemble ? { scale: 0 } : undefined}
        animate={assemble ? { scale: 1 } : undefined}
        transition={assemble ? { duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] } : undefined}
      />
      <motion.circle
        cx={ORIGIN[0]}
        cy={ORIGIN[1]}
        r="12"
        fill="none"
        stroke="#1E4D38"
        strokeOpacity="0.35"
        strokeWidth="1"
        initial={assemble ? { scale: 0, opacity: 0 } : undefined}
        animate={assemble ? { scale: 1, opacity: 1 } : undefined}
        transition={assemble ? { duration: 0.7, delay: delay + 0.15 } : undefined}
      />
    </svg>
  );
}

/* ---------------------------------------------------------------- */
/*  Research-creation loop: Investigate, Translate, Apply, Learn,    */
/*  with a dashed return path.                                       */
/* ---------------------------------------------------------------- */

const LOOP_STAGES = ["INVESTIGATE", "TRANSLATE", "APPLY", "LEARN"];
const LOOP_X = [70, 240, 410, 580];
const LOOP_Y = 90;

export function ProcessLoop({ active = 0, className = "" }) {
  return (
    <svg viewBox="0 0 640 220" className={className} role="img" aria-label="Investigate, translate, apply, learn, and back again">
      {LOOP_X.slice(0, -1).map((x, i) => (
        <motion.line
          key={i}
          x1={x + 10}
          y1={LOOP_Y}
          x2={LOOP_X[i + 1] - 10}
          y2={LOOP_Y}
          stroke={i < active ? "#1E4D38" : "#0F0F0F"}
          strokeOpacity={i < active ? 0.8 : 0.2}
          strokeWidth="1.5"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 + i * 0.25 }}
        />
      ))}
      {/* return path: Learn back to Investigate */}
      <motion.path
        d={`M${LOOP_X[3]},${LOOP_Y + 14} C 560,190 80,190 ${LOOP_X[0]},${LOOP_Y + 14}`}
        fill="none"
        stroke="#6E9B82"
        strokeWidth="1"
        strokeDasharray="3 5"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.2, delay: 1.1 }}
      />
      <motion.circle r="3" fill="#6E9B82" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 1.2 }}>
        <animateMotion
          dur="6s"
          repeatCount="indefinite"
          path={`M${LOOP_X[3]},${LOOP_Y + 14} C 560,190 80,190 ${LOOP_X[0]},${LOOP_Y + 14}`}
        />
      </motion.circle>
      {LOOP_X.map((x, i) => (
        <g key={i}>
          <motion.circle
            cx={x}
            cy={LOOP_Y}
            r={i === active ? 6 : 4}
            fill={i === active ? "#1E4D38" : "#F7F6F2"}
            stroke={i <= active ? "#1E4D38" : "#0F0F0F"}
            strokeOpacity={i <= active ? 1 : 0.3}
            strokeWidth="1.5"
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.15 + i * 0.25 }}
            style={{ transition: "r 0.4s ease" }}
          />
          <text
            x={x}
            y={LOOP_Y - 24}
            textAnchor="middle"
            fontSize="10"
            letterSpacing="2.5"
            fill={i === active ? "#0F0F0F" : "#6B6A65"}
          >
            {LOOP_STAGES[i]}
          </text>
        </g>
      ))}
      <text x="320" y="205" textAnchor="middle" fontSize="10" letterSpacing="2" fill="#6B6A65" fontStyle="italic">
        new observations return as new questions
      </text>
    </svg>
  );
}

/* ---------------------------------------------------------------- */
/*  Ecosystem: one centre, four expressions.                         */
/*  Mentheris at the centre; four thin organic paths connect it to   */
/*  Research, Practice, Community and Products. Hover isolates one   */
/*  relationship at a time; hovering the centre shows them all.      */
/* ---------------------------------------------------------------- */

const ECO_PATHS = [
  "M320,205 C 255,175 200,125 142,92",
  "M320,205 C 385,185 445,110 518,64",
  "M320,205 C 268,255 224,290 172,318",
  "M320,205 C 392,248 442,295 498,328",
];
const ECO_NODES = [
  [142, 92],
  [518, 64],
  [172, 318],
  [498, 328],
];
const ECO_POS = [
  "left-[1%] top-[10%] text-left",
  "right-[1%] top-[2%] text-right",
  "left-[6%] bottom-[8%] text-left",
  "right-[3%] bottom-[2%] text-right",
];

export function EcosystemSystem({ items }) {
  const [hover, setHover] = useState(null); // null | "centre" | index
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  const pathOpacity = (i) =>
    hover === null ? 0.22 : hover === "centre" ? 0.4 : hover === i ? 0.7 : 0.07;
  const nodeOpacity = (i) =>
    hover === null || hover === "centre" ? 0.45 : hover === i ? 1 : 0.18;
  const quiet = (i) => hover !== null && hover !== "centre" && hover !== i;

  return (
    <>
      {/* desktop / tablet: one centre, four trajectories */}
      <div ref={ref} className="relative hidden md:block h-[340px] lg:h-[380px]" data-testid="ecosystem-map">
        <svg viewBox="0 0 640 400" className="absolute inset-0 w-full h-full" aria-hidden="true">
          {ECO_PATHS.map((d, i) => (
            <motion.path
              key={i}
              d={d}
              fill="none"
              strokeWidth="1"
              initial={{ opacity: 0 }}
              animate={{ opacity: inView ? pathOpacity(i) : 0, stroke: hover === i ? "#1E4D38" : "#0F0F0F" }}
              transition={{ duration: 0.7, ease: "easeOut" }}
            />
          ))}
          {ECO_NODES.map(([x, y], i) => (
            <motion.circle
              key={i}
              cx={x}
              cy={y}
              r="3"
              initial={{ opacity: 0 }}
              animate={{ opacity: inView ? nodeOpacity(i) : 0, fill: hover === i ? "#1E4D38" : "#0F0F0F" }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          ))}
          {/* Mentheris centre: always active */}
          <motion.circle
            cx="320"
            cy="205"
            r="5"
            fill="#1E4D38"
            initial={{ scale: 0 }}
            animate={inView ? { scale: 1 } : {}}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          />
          <motion.circle
            cx="320"
            cy="205"
            r="14"
            fill="none"
            stroke="#1E4D38"
            strokeOpacity="0.3"
            strokeWidth="1"
            initial={{ scale: 0, opacity: 0 }}
            animate={inView ? { scale: 1, opacity: 1 } : {}}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          />
        </svg>

        <div
          className="absolute left-1/2 top-[51%] -translate-x-1/2 -translate-y-1/2 cursor-default px-6 py-8"
          onMouseEnter={() => setHover("centre")}
          onMouseLeave={() => setHover(null)}
          data-testid="ecosystem-centre"
        >
          <span className="block pt-9 font-serif text-xl md:text-2xl tracking-tight">Mentheris</span>
        </div>

        {items.map((item, i) => {
          const inner = (
            <div className="group max-w-[220px]" >
              <span className={`flex items-center gap-2 ${i === 1 || i === 3 ? "justify-end" : ""}`}>
                <span
                  className={`w-2 h-2 rounded-full shrink-0 transition-colors duration-500 ${hover === i ? "bg-ember" : "bg-ink/30"}`}
                  aria-hidden="true"
                />
                <span className="font-serif text-xl md:text-2xl tracking-tight">{item.label}</span>
              </span>
              <span className="block mt-2 text-sm text-fog leading-relaxed">{item.text}</span>
            </div>
          );
          return (
            <motion.div
              key={item.label}
              className={`absolute ${ECO_POS[i]} transition-opacity duration-500 ${quiet(i) ? "opacity-45" : "opacity-100"}`}
              initial={{ opacity: 0, y: 10 }}
              animate={inView ? { opacity: quiet(i) ? 0.45 : 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.25 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            >
              {item.external ? (
                <a href={item.to} data-testid={`ecosystem-${item.label.toLowerCase()}`}>{inner}</a>
              ) : (
                <Link to={item.to} data-testid={`ecosystem-${item.label.toLowerCase()}`}>{inner}</Link>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* mobile: central spine with vertical branches */}
      <div className="md:hidden" data-testid="ecosystem-map-mobile">
        <div className="flex items-center gap-3">
          <span className="w-2.5 h-2.5 rounded-full bg-ember shrink-0" aria-hidden="true" />
          <span className="font-serif text-xl tracking-tight">Mentheris</span>
        </div>
        <div className="ml-[5px] mt-4 border-l border-ink/15 pl-7 space-y-8">
          {items.map((item) => {
            const inner = (
              <div className="relative">
                <span className="absolute -left-7 top-[13px] w-5 h-px bg-ink/15" aria-hidden="true" />
                <span className="absolute -left-7 top-[10px] w-1.5 h-1.5 rounded-full bg-ink/30 -translate-x-[3px]" aria-hidden="true" />
                <span className="font-serif text-xl tracking-tight">{item.label}</span>
                <span className="block mt-1.5 text-sm text-fog leading-relaxed max-w-[260px]">{item.text}</span>
              </div>
            );
            return item.external ? (
              <a key={item.label} href={item.to} data-testid={`ecosystem-m-${item.label.toLowerCase()}`} className="block">{inner}</a>
            ) : (
              <Link key={item.label} to={item.to} data-testid={`ecosystem-m-${item.label.toLowerCase()}`} className="block">{inner}</Link>
            );
          })}
        </div>
      </div>
    </>
  );
}

/* Vision: the network expands outward */
export function ExpandingNetwork({ className = "" }) {
  return (
    <svg viewBox="0 0 320 220" className={className} aria-hidden="true">
      {[46, 76, 106].map((r, i) => (
        <motion.circle
          key={r}
          cx="160"
          cy="110"
          r={r}
          fill="none"
          stroke="#0F0F0F"
          strokeOpacity="0.12"
          strokeWidth="1"
          initial={{ scale: 0.4, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.1, delay: i * 0.25, ease: [0.22, 1, 0.36, 1] }}
          style={{ transformOrigin: "160px 110px" }}
        />
      ))}
      {[
        [160, 110],
        [160, 64],
        [206, 136],
        [114, 136],
        [236, 84],
        [84, 84],
        [236, 150],
        [84, 150],
        [160, 4 + 12],
      ].map(([x, y], i) => (
        <motion.circle
          key={i}
          cx={x}
          cy={y}
          r={i === 0 ? 4.5 : 2.5}
          fill={i === 0 ? "#1E4D38" : "#0F0F0F"}
          fillOpacity={i === 0 ? 1 : 0.5}
          initial={{ scale: 0 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.3 + i * 0.12 }}
        />
      ))}
    </svg>
  );
}
